import { FC, useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { bankInitialValues } from 'src/utils/initialValues.utils';
import { bankValidationSchema } from 'src/utils/validationScheme.utils';
import { apiUrls } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import BankGrid from 'src/components/Grids/BankGrid';
import AccountTypeField from 'src/components/InputFields/BankAccountType';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import SearchBar from 'src/components/InputFields/Searchbar';
import {
  PrimaryActionButton,
  ButtonIcon,
  OverflowContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import { fetchBankNames } from 'src/services/enums.service';

const BankAccountManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [bankAdded, setBankAdded] = useState(false);
  const { showSnackbar } = StyledSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [bankOptions, setBankOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const fetchBankList = async () => {
    try {
      const response = await fetchBankNames();
      const options = response.map((value: string) => ({
        label: value,
        value: value,
      }));
      console.log({ options });
      setBankOptions(options);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const formik = useFormik({
    initialValues: bankInitialValues,
    validationSchema: bankValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const getDialogHeight = () =>
    calculateDialogHeight(formik, [
      ['bankName', 'branchName'],
      ['accountName', 'accountNumber'],
      ['accountType'],
    ]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const addBank = await axiosTokenInstance.post(apiUrls.ADD_BANK, {
        bankName: values.bankName,
        branchName: values.branchName,
        accountName: values.accountName,
        accountNumber: values.accountNumber,
        accountType: values.accountType,
        status: 'Active',
      });

      showSnackbar(addBank.data.message, 'success');
      setBankAdded(!bankAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = () => {
    fetchBankList();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  return (
    <MainLayout
      title="Bank Account Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search banks by Account Name"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="200px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Bank Account
        </PrimaryActionButton>
      }
    >
      <BankGrid state={bankAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title="Add New Bank Account"
            subtitle="Fill in the details below to add a new bank account to the system."
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <FormikSelectField
                  id="bankName"
                  label="Bank Name"
                  formikProps={formik}
                  options={bankOptions}
                  width="300px"
                />

                <TextField
                  width="300px"
                  id="branchName"
                  label="Branch"
                  formikProps={formik}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" gap={2}>
                <TextField
                  width="300px"
                  id="accountName"
                  label="Account Holder’s Name"
                  formikProps={formik}
                />

                <TextField
                  width="300px"
                  id="accountNumber"
                  label="Account Number"
                  formikProps={formik}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" gap={2}>
                <AccountTypeField
                  width="300px"
                  id="accountType"
                  label="Account Type"
                  formikProps={formik}
                />
              </Box>
            </Box>
          </CustomDialog>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default BankAccountManagement;
